import type { AccountAccess } from './accountAccess'

interface PlanRow {
  key: 'contexts' | 'images'
  label: string
  full: string
}

const ROWS: PlanRow[] = [
  { key: 'contexts', label: 'Contexts', full: 'Delete a Context to make room for a new one.' },
  { key: 'images', label: 'Images', full: 'Delete an image or a note with images to add more.' },
]

/**
 * Usage against the plan's limits other than notes (Contexts, images, notes per Context), shown in
 * Settings → Account. A limit the plan does not set (null) is not shown. The numbers are the server's.
 */
export function AccountPlanUsage({ access, contextsEnabled = false }: { access: AccountAccess; contextsEnabled?: boolean }) {
  const { capacities, usage } = access
  const rows = ROWS.filter((row) => capacities[row.key] !== null && (row.key !== 'contexts' || contextsEnabled))
  if (rows.length === 0 && (!contextsEnabled || capacities.notesPerContext === null)) return null
  return (
    <div className="settings-account-access__limits">
      {rows.map((row) => {
        const limit = capacities[row.key] as number
        const used = usage[row.key]
        const atLimit = used >= limit
        return (
          <div key={row.key} className="settings-account-access__limit">
            <p>{used.toLocaleString()} of {limit.toLocaleString()} {row.label}</p>
            <progress
              className={`progress ${atLimit ? 'progress-warning' : 'progress-info'}`}
              value={Math.min(used, limit)}
              max={limit}
              aria-label={`${used} of ${limit} ${row.label} used`}
            />
            {atLimit ? (
              <div role="alert" className="alert alert-soft alert-warning settings-account-access__warning">
                You have reached your limit of {limit.toLocaleString()} {row.label}. {row.full} Everything you have stays available.
              </div>
            ) : null}
          </div>
        )
      })}
      {contextsEnabled && capacities.notesPerContext !== null ? (
        <p>Each Context can hold up to {capacities.notesPerContext.toLocaleString()} notes.</p>
      ) : null}
    </div>
  )
}
